/**
 * Phase 261m + 261n — Academies-at-risk roster for the Push Engagement tile.
 *
 * Phase 261m: shows academies whose delivery rate falls below threshold.
 * Phase 261n: each row now has a "Diagnose & email owner" button that fires
 * `POST /api/super-admin/adoption/academy-diagnose-outreach`, persists a
 * diagnosis row, and emails the academy owner with tailored remediation copy.
 *
 * Honest UX:
 *   • If the parent passes an empty array, we return null — never a fake
 *     "no risks detected" celebration banner.
 *   • Row metrics are displayed verbatim from the backend payload (no
 *     client-side rate recompute that could drift).
 *   • The Diagnose action surfaces the REAL diagnosis result (primary_fault
 *     + metrics + email_sent flag) — never pretends an email succeeded when
 *     the backend reports otherwise.
 *   • Idempotent within 24h server-side; the UI badges "Diagnosed Xh ago"
 *     instead of re-triggering on accidental double-click.
 */
import { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  ShieldAlert, Radio, ExternalLink, Users, Zap, Loader2,
  CheckCircle2, AlertTriangle, Mail, MailX, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

function fmt(n) {
  if (n == null) return '—';
  if (typeof n !== 'number') return String(n);
  return n.toLocaleString();
}

function pct(n) {
  if (n == null || isNaN(n)) return '—';
  return `${Number(n).toFixed(1)}%`;
}

function formatRelativeTime(iso) {
  if (!iso) return '';
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000) return 'just now';
    if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
    if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
    return `${Math.floor(ms / 86_400_000)}d ago`;
  } catch (_) { return ''; }
}

const FAULT_LABEL = {
  NO_ACTIVE_SUBSCRIPTIONS: 'No active subscriptions',
  STALE_SUBSCRIPTIONS_PURGED: 'Stale subscriptions purged',
  HIGH_OPT_OUT_RATE: 'High opt-out rate',
  PUSH_GATEWAY_ERRORS: 'Push gateway errors',
  UNKNOWN: 'Unknown',
};

function RiskRow({ row, idx }) {
  const [diagnosing, setDiagnosing] = useState(false);
  const [result, setResult] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const isOutage = !!row.is_outage || row.delivery_rate_pct === 0;
  const academiesHref = row.academy_slug
    ? `/super-admin/academies?search=${encodeURIComponent(row.academy_slug)}`
    : `/super-admin/academies?search=${encodeURIComponent(row.academy_id || '')}`;

  const handleDiagnose = async () => {
    setDiagnosing(true);
    try {
      const { data } = await api.post('/super-admin/adoption/academy-diagnose-outreach', {
        academy_id: row.academy_id,
      });
      setResult(data);
      setExpanded(true);
      if (data?.deduped) {
        toast.info(`Diagnosed ${formatRelativeTime(data.last_diagnosed_at)} — not re-emailing within 24h.`);
      } else if (data?.email_sent) {
        toast.success(`Diagnosis sent to ${data.email_recipient || 'the academy owner'}.`);
      } else if (data?.email_skipped_reason === 'owner_email_missing') {
        toast.warning('Diagnosis logged but no owner email on file — manual outreach needed.');
      } else {
        toast.warning(`Diagnosis logged but email failed: ${data?.email_skipped_reason || 'unknown reason'}`);
      }
    } catch (err) {
      toast.error(`Diagnose failed: ${err?.response?.data?.detail || err.message || 'unknown error'}`);
    } finally {
      setDiagnosing(false);
    }
  };

  const showAck = !!result;

  return (
    <>
      <tr
        className={isOutage ? 'bg-rose-100/50 dark:bg-rose-900/20' : ''}
        data-testid={`academies-at-risk-row-${idx}`}
      >
        <td className="p-2">
          <div className="flex items-center gap-1.5">
            {isOutage && (
              <Radio className="h-3 w-3 text-rose-600 dark:text-rose-400 shrink-0" data-testid={`outage-icon-${idx}`} />
            )}
            <div>
              <div className="font-bold text-slate-900 dark:text-slate-100">
                {row.academy_name || '(unknown)'}
              </div>
              <div className="text-[9px] font-mono text-slate-400 dark:text-slate-500">
                {row.academy_id}
              </div>
            </div>
          </div>
        </td>
        <td className="p-2 text-right tabular-nums">{fmt(row.events)}</td>
        <td className="p-2 text-right tabular-nums">{fmt(row.dispatched)}</td>
        <td className="p-2 text-right tabular-nums">{fmt(row.delivered)}</td>
        <td className="p-2 text-right tabular-nums">{fmt(row.muted)}</td>
        <td className="p-2 text-right">
          <span
            className={`inline-block px-2 py-0.5 rounded font-bold tabular-nums ${
              isOutage
                ? 'bg-rose-600 text-white'
                : 'bg-rose-50 dark:bg-rose-900/40 text-rose-700 dark:text-rose-200 border border-rose-200 dark:border-rose-800'
            }`}
          >
            {pct(row.delivery_rate_pct)}
          </span>
        </td>
        <td className="p-2 pr-3 text-right">
          <div className="inline-flex items-center gap-1">
            <button
              type="button"
              onClick={handleDiagnose}
              disabled={diagnosing}
              className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-900 dark:border-slate-100 bg-slate-900 dark:bg-slate-100 hover:bg-slate-800 dark:hover:bg-slate-200 text-white dark:text-slate-900 text-[10px] font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Snapshot diagnosis + email academy owner"
              data-testid={`academies-at-risk-diagnose-${idx}`}
            >
              {diagnosing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
              <span>{diagnosing ? 'Diagnosing…' : 'Diagnose'}</span>
            </button>
            <a
              href={academiesHref}
              className="inline-flex items-center gap-1 px-1.5 py-1 rounded border border-slate-200 dark:border-slate-700 hover:bg-white dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
              title={`Open ${row.academy_name || row.academy_id} support profile`}
              data-testid={`academies-at-risk-action-${idx}`}
            >
              <Users className="h-3 w-3" />
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </td>
      </tr>

      {/* Phase 261n — collapsible diagnosis result panel */}
      {showAck && (
        <tr
          data-testid={`academies-at-risk-diagnosis-result-${idx}`}
          className={result.deduped ? 'bg-amber-50/40 dark:bg-amber-900/10' : 'bg-emerald-50/40 dark:bg-emerald-900/10'}
        >
          <td colSpan={7} className="p-3">
            <div className="space-y-2 text-[11px]">
              <button
                type="button"
                className="flex items-center justify-between w-full text-left"
                onClick={() => setExpanded((v) => !v)}
                data-testid={`academies-at-risk-toggle-${idx}`}
              >
                <div className="flex items-center gap-2">
                  {result.email_sent ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                  ) : (
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                  )}
                  <span className="font-bold text-slate-900 dark:text-slate-100">
                    {FAULT_LABEL[result.primary_fault] || result.primary_fault}
                  </span>
                  {result.deduped && (
                    <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200">
                      cached {formatRelativeTime(result.last_diagnosed_at)}
                    </Badge>
                  )}
                  {result.email_sent ? (
                    <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">
                      <Mail className="h-2.5 w-2.5 mr-0.5" /> Emailed {result.email_recipient}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[9px] bg-rose-50 text-rose-700 border-rose-200">
                      <MailX className="h-2.5 w-2.5 mr-0.5" /> Not emailed
                    </Badge>
                  )}
                </div>
                {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </button>

              {expanded && (
                <div className="pl-5 space-y-2 text-slate-700 dark:text-slate-300">
                  <p className="leading-snug">{result.remediation}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] font-mono">
                    <span>events: <strong>{result.metrics?.events}</strong></span>
                    <span>dispatched: <strong>{result.metrics?.dispatched}</strong></span>
                    <span>delivered: <strong>{result.metrics?.delivered}</strong></span>
                    <span>removed_stale: <strong>{result.metrics?.removed_stale}</strong></span>
                    <span>muted_skips: <strong>{result.metrics?.muted_skips}</strong></span>
                    <span>errors: <strong>{result.metrics?.errors_count}</strong></span>
                    <span>active_subs: <strong>{result.metrics?.active_subscriptions}</strong></span>
                  </div>
                  {result.email_skipped_reason && (
                    <p className="text-[10px] text-rose-700 dark:text-rose-300">
                      Email skipped: <code>{result.email_skipped_reason}</code>
                    </p>
                  )}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function AcademiesAtRiskCard({ rows = [], threshold = 50, minDispatched = 5 }) {
  if (!Array.isArray(rows) || rows.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="academies-at-risk-card">
      <div className="text-[11px] uppercase font-bold text-rose-700 dark:text-rose-300 tracking-wider flex items-center gap-1.5">
        <ShieldAlert className="h-3 w-3" />
        Academies at risk
        <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-200 dark:border-rose-800">
          {rows.length}
        </Badge>
        <span className="ml-1 text-[10px] font-normal text-slate-500 dark:text-slate-400 normal-case">
          (delivery &lt; {threshold}%, min {minDispatched} dispatches in window)
        </span>
      </div>

      <div className="border border-rose-200 dark:border-rose-900/50 rounded-lg overflow-hidden bg-rose-50/30 dark:bg-rose-900/10">
        <table className="w-full text-xs">
          <thead className="bg-rose-50 dark:bg-rose-900/30 text-[10px] uppercase font-bold text-rose-700/80 dark:text-rose-200/80 tracking-wider">
            <tr>
              <th className="text-left p-2">Academy</th>
              <th className="text-right p-2">Events</th>
              <th className="text-right p-2">Dispatched</th>
              <th className="text-right p-2">Delivered</th>
              <th className="text-right p-2">Muted</th>
              <th className="text-right p-2">Delivery %</th>
              <th className="text-right p-2 pr-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rose-100 dark:divide-rose-900/40">
            {rows.map((row, idx) => (
              <RiskRow key={`${row.academy_id}-${idx}`} row={row} idx={idx} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
